import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../contextos/AuthContext";
import { estilosGrupos } from "../elementos/gruposEstilos";
import {
  buscarMembresia,
  guardarInvitacionPendiente,
  limpiarInvitacionPendiente,
  normalizarCodigo,
  obtenerInvitacion,
  solicitarIngreso,
} from "../funciones/gruposServicio";

/*
Estados de la pantalla:
  cargando | invalida | inactiva | lista | enviando | resultado
*/

export default function UnirseGrupo() {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const { usuario, cargando: cargandoAuth } = useAuth();

  const [estado, setEstado] = useState("cargando");
  const [invitacion, setInvitacion] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");

  // Leer la invitación (funciona sin sesión)
  useEffect(() => {
    let activo = true;

    obtenerInvitacion(codigo)
      .then((inv) => {
        if (!activo) return;
        if (!inv) {
          limpiarInvitacionPendiente();
          setEstado("invalida");
          return;
        }
        if (!inv.activa) {
          limpiarInvitacionPendiente();
          setEstado("inactiva");
          return;
        }
        setInvitacion(inv);
        setEstado("lista");
      })
      .catch((err) => {
        console.error("Error leyendo invitación:", err);
        if (activo) {
          setError("No fue posible abrir la invitación. Revisa tu conexión e inténtalo de nuevo.");
          setEstado("invalida");
        }
      });

    return () => {
      activo = false;
    };
  }, [codigo]);

  // Con sesión: ya llegó a la invitación, así que se limpia la pendiente
  // y se revisa si ya pertenece o ya había solicitado.
  useEffect(() => {
    if (!usuario || !invitacion) return;

    limpiarInvitacionPendiente();

    buscarMembresia(invitacion.grupoId, usuario.uid)
      .then((m) => {
        if (m && (m.estado === "activo" || m.estado === "pendiente")) {
          setResultado({ estado: m.estado, rol: m.rol, yaExistia: true });
          setEstado("resultado");
        }
      })
      .catch(() => {
        // Si falla la consulta, se deja solicitar normalmente
      });
  }, [usuario, invitacion]);

  const irA = (ruta) => {
    guardarInvitacionPendiente(normalizarCodigo(codigo));
    navigate(ruta);
  };

  const solicitar = async () => {
    setEstado("enviando");
    setError("");
    try {
      setResultado(await solicitarIngreso(invitacion, usuario.uid));
      setEstado("resultado");
    } catch (err) {
      console.error("Error solicitando ingreso:", err);
      setError("No fue posible enviar tu solicitud. Es posible que este enlace ya no esté activo; pide uno nuevo a tu profesor.");
      setEstado("lista");
    }
  };

  const comoProfesor = invitacion?.rol === "profesor";

  return (
    <div className="gg gg-portada">
      <style>{estilosGrupos}</style>

      <main className="gg-portada__tarjeta">
        <span className="gg-sutil">Invitación a Yutake</span>

        {(estado === "cargando" || cargandoAuth) && <p className="gg-sutil">Abriendo invitación…</p>}

        {estado === "invalida" && !cargandoAuth && (
          <>
            <h1 className="gg-portada__grupo">Esta invitación no existe</h1>
            <p className="gg-sutil">
              {error || "Revisa que el enlace o el código estén completos, o pide uno nuevo a tu profesor."}
            </p>
            <button type="button" className="gg-btn gg-btn--ancho" onClick={() => navigate("/")}>
              Ir a Yutake
            </button>
          </>
        )}

        {estado === "inactiva" && !cargandoAuth && (
          <>
            <h1 className="gg-portada__grupo">Este enlace ya no está activo</h1>
            <p className="gg-sutil">El profesor lo renovó o el grupo ya no recibe nuevos miembros. Pídele un enlace nuevo.</p>
            <button type="button" className="gg-btn gg-btn--ancho" onClick={() => navigate("/")}>
              Ir a Yutake
            </button>
          </>
        )}

        {(estado === "lista" || estado === "enviando") && invitacion && !cargandoAuth && (
          <>
            <h1 className="gg-portada__grupo">{invitacion.grupoNombre}</h1>
            <p>
              {invitacion.profesorNombre ? `${invitacion.profesorNombre} te invita` : "Te invitaron"} a unirte{" "}
              {comoProfesor ? "como profesor, para acompañar el avance de los estudiantes." : "como estudiante, para preparar tu examen con el grupo."}
            </p>

            {error && <p role="alert" className="gg-alerta">{error}</p>}

            {usuario ? (
              <>
                <button
                  type="button"
                  className="gg-btn gg-btn--primario gg-btn--ancho"
                  onClick={solicitar}
                  disabled={estado === "enviando"}
                >
                  {estado === "enviando" ? "Enviando solicitud…" : "Solicitar unirme"}
                </button>
                <p className="gg-sutil">El administrador del grupo revisará tu solicitud.</p>
              </>
            ) : (
              <>
                <p className="gg-sutil">
                  Yutake te ayuda a prepararte para tus exámenes de admisión. Crea tu cuenta y volverás aquí para enviar tu solicitud.
                </p>
                <button type="button" className="gg-btn gg-btn--primario gg-btn--ancho" onClick={() => irA("/crear-cuenta")}>
                  Crear mi cuenta
                </button>
                <button type="button" className="gg-btn gg-btn--ancho" onClick={() => irA("/iniciar-sesion")}>
                  Ya tengo cuenta
                </button>
              </>
            )}
          </>
        )}

        {estado === "resultado" && resultado && (
          <>
            <h1 className="gg-portada__grupo">
              {resultado.estado === "activo" ? "Ya eres parte del grupo" : "Solicitud enviada"}
            </h1>
            <p>
              {resultado.estado === "activo"
                ? `Ya perteneces a "${invitacion?.grupoNombre}".`
                : resultado.yaExistia
                  ? `Tu solicitud para "${invitacion?.grupoNombre}" está esperando la aprobación del administrador.`
                  : `Listo. Cuando el administrador apruebe tu solicitud, entrarás a "${invitacion?.grupoNombre}".`}
            </p>
            {resultado.rol === "profesor" && (
              <p className="gg-sutil">
                Para ver el grupo cuando seas aprobado, cambia a tu perfil de profesor desde tu panel principal.
              </p>
            )}
            <button type="button" className="gg-btn gg-btn--primario gg-btn--ancho" onClick={() => navigate("/")}>
              Continuar a mi panel
            </button>
          </>
        )}
      </main>
    </div>
  );
}